import React, { useEffect, useState } from 'react';
import { useApiCaller } from '../../utils/ApiUtilities';
import { getSuperAdminQuestionAuditUrl } from '../../utils/UrlUtilities';
import { formatBdPst } from '../../utils/DateUtilities';

function ActionBadge({ action }) {
    const styles = {
        CREATED: 'bg-primary',
        UPDATED: 'bg-info text-dark',
        REVIEWED: 'bg-success',
        MARKED_FOR_REVISION: 'bg-warning text-dark',
    };
    return <span className={`badge ${styles[action] || 'bg-secondary'}`}>{action}</span>;
}

function JsonBlock({ data }) {
    if (!data) return <span className="text-muted">—</span>;
    return (
        <pre className="bg-light p-2 mb-0" style={{ fontSize: '0.75rem', maxHeight: 200, overflow: 'auto' }}>
            {JSON.stringify(data, null, 2)}
        </pre>
    );
}

export default function QuestionAuditDrawer({ questionId, onClose }) {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState({});
    const CallApi = useApiCaller();

    useEffect(() => {
        if (!questionId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            const res = await CallApi({
                url: getSuperAdminQuestionAuditUrl(questionId),
                method: 'GET',
            });
            if (!cancelled) {
                if (res.success) setEntries(res.data || []);
                setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [questionId]);

    if (!questionId) return null;

    return (
        <div
            className="position-fixed top-0 end-0 h-100 bg-white shadow"
            style={{ width: 480, zIndex: 1050, overflowY: 'auto' }}
        >
            <div className="d-flex justify-content-between align-items-center p-3 border-bottom">
                <h5 className="mb-0">History — Question #{questionId}</h5>
                <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>Close</button>
            </div>
            <div className="p-3">
                {loading && <div>Loading…</div>}
                {!loading && entries.length === 0 && (
                    <div className="text-muted">No audit entries yet.</div>
                )}
                {entries.map((e) => (
                    <div key={e.id} className="card mb-3">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <ActionBadge action={e.action} />
                                <small className="text-muted">{formatBdPst(e.at)}</small>
                            </div>
                            <div className="mb-2">
                                <strong>{e.editor_name || 'Unknown'}</strong>{' '}
                                <small className="text-muted">({e.editor_role || 'unknown'})</small>
                            </div>
                            {e.note && (
                                <div className="alert alert-warning py-2 mb-2">{e.note}</div>
                            )}
                            <button
                                className="btn btn-sm btn-link p-0"
                                onClick={() => setExpanded(s => ({ ...s, [e.id]: !s[e.id] }))}
                            >
                                {expanded[e.id] ? 'Hide diff' : 'Show diff'}
                            </button>
                            {expanded[e.id] && (
                                <div className="mt-2">
                                    <div className="text-muted small">Before</div>
                                    <JsonBlock data={e.before_json} />
                                    <div className="text-muted small mt-2">After</div>
                                    <JsonBlock data={e.after_json} />
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
